/* Dirty Penny reviews — shared data + rendering.
   Judge.me is a data source, never something we style. Rows come from its
   own widget JSON, which (unlike the mounted widget, fixed at 5) honours
   per_page, so the design's three-a-page card is possible at all.

   Used by sections/dp-product-reviews.liquid and snippets/dp-reviews-modal.
   Markup and type live in assets/dp-reviews.css.

   window.DPReviews:
     fetchPage(productId, page, perPage) -> Promise<{reviews, page, pages,
                                                     average, count}> (cached)
     warm(productId, perPage)            -> prefetch page 1, never rejects
     skeleton(container, n)              -> shimmer rows in the real geometry
     paint(container, reviews)           -> number of rows written
     pager(el, current, total, go)       -> builds the page numbers
     stars(n) · ago(iso) · cap(name) · esc(s)
*/
window.DPReviews = (function () {
  var API = 'https://cdn.judge.me/reviews/reviews_for_widget';
  var STAR = '<path d="M2.17985 8.23983L6.09984 12.0598L5.17984 17.4398L9.99982 14.8998L14.8198 17.4398L13.8998 12.0598L17.8198 8.23983L12.4198 7.45983L9.99982 2.57986L7.57983 7.45983L2.17985 8.23983Z" stroke-width="1" stroke-linejoin="round"/>';
  var shop = (document.documentElement.getAttribute('data-dp-shop') || '').trim();
  var cache = {};

  function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML; }
  /* Judge.me hands back body_html; we only ever want the words, both because
     the row is a four-line clamp and because it keeps their markup out of
     our DOM */
  function plain(html) { var d = document.createElement('div'); d.innerHTML = html || ''; return (d.textContent || '').replace(/\s+/g, ' ').trim(); }
  /* reviewers type their name however they like and Judge.me keeps it
     verbatim — "rose", "anna k." Only ever raise a lowercase first letter,
     so "McDonald" and "de Vries" survive intact. */
  function cap(s) { return (s || '').replace(/\b[a-z]/g, function (c) { return c.toUpperCase(); }); }

  /* the five <svg>s only — callers own the .dp-pr-stars wrapper, so this can
     also refresh an existing one in place */
  function stars(n) {
    var h = '';
    for (var i = 1; i <= 5; i++) {
      h += '<svg class="dp-pr-star' + (i <= n ? '' : ' dp-pr-star--off') +
           '" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">' + STAR + '</svg>';
    }
    return h;
  }

  function ago(iso) {
    var t = Date.parse(iso);
    if (isNaN(t)) return '';
    var days = Math.floor((Date.now() - t) / 86400000);
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 30) return days + ' days ago';
    var months = Math.floor(days / 30.44);
    if (months < 12) return months + (months === 1 ? ' month ago' : ' months ago');
    var years = Math.floor(days / 365.25);
    return years + (years === 1 ? ' year ago' : ' years ago');
  }

  function fetchPage(productId, page, perPage) {
    var key = productId + ':' + page + ':' + perPage;
    if (cache[key]) return Promise.resolve(cache[key]);
    var url = API + '?product_id=' + productId + '&page=' + page + '&per_page=' + perPage +
              '&sort_by=created_at&sort_dir=desc&skip_other_languages=true' +
              '&widget_theme=standard&shop_domain=' + encodeURIComponent(shop) + '&platform=shopify';
    return fetch(url, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (j) {
        var pg = j.pagination || {};
        var out = {
          reviews: (j.reviews || []).map(function (r) {
            return {
              rating: Math.round(r.rating || 0),
              name: cap(r.reviewer_name || ''),
              date: ago(r.created_at),
              verified: !!r.verified_buyer,
              title: (r.title || '').trim(),
              body: plain(r.body_html)
            };
          }),
          page: pg.current_page || page,
          pages: pg.total_pages || 0,
          average: parseFloat(j.average_rating),
          count: parseInt(j.number_of_reviews, 10)
        };
        cache[key] = out;
        return out;
      });
  }

  /* the same row, greyed out, so the card is never an empty box while the
     request is in flight */
  function skeleton(box, n) {
    var h = '';
    for (var i = 0; i < n; i++) {
      h += '<div class="dp-pr-row dp-pr-row--sk" aria-hidden="true">' +
             '<span class="dp-pr-sk dp-pr-sk-badge"></span>' +
             '<span class="dp-pr-sk dp-pr-sk-name"></span>' +
             '<div class="dp-pr-rowhead"><span class="dp-pr-sk dp-pr-sk-stars"></span>' +
               '<span class="dp-pr-sk dp-pr-sk-date"></span></div>' +
             '<div class="dp-pr-rowbody"><span class="dp-pr-sk dp-pr-sk-title"></span>' +
               '<span class="dp-pr-sk dp-pr-sk-line"></span>' +
               '<span class="dp-pr-sk dp-pr-sk-line"></span>' +
               '<span class="dp-pr-sk dp-pr-sk-line"></span></div>' +
           '</div>';
    }
    box.classList.remove('is-fresh');
    box.innerHTML = h;
    box.setAttribute('aria-busy', 'true');
  }

  /* page 1 on hover/focus of a trigger: by the time the click lands the
     answer is usually already in the cache */
  function warm(productId, perPage) {
    return fetchPage(productId, 1, perPage).catch(function () {});
  }

  function paint(box, list) {
    var frag = document.createDocumentFragment();
    list.forEach(function (r) {
      if (!r.body && !r.title) return;
      var row = document.createElement('div');
      row.className = 'dp-pr-row';
      row.style.setProperty('--i', frag.childNodes.length);
      var h = '';
      if (r.verified) h += '<span class="dp-pr-badge">Verified</span>';
      if (r.name) h += '<p class="dp-pr-name">' + esc(r.name) + '</p>';
      h += '<div class="dp-pr-rowhead"><span class="dp-pr-stars" aria-hidden="true">' + stars(r.rating) +
           '</span><span class="dp-pr-date">' + esc(r.date) + '</span></div>';
      h += '<div class="dp-pr-rowbody">';
      if (r.title) h += '<p class="dp-pr-rtitle">' + esc(r.title) + '</p>';
      h += '<p class="dp-pr-rtext">' + esc(r.body) + '</p></div>';
      row.innerHTML = h;
      frag.appendChild(row);
    });
    box.textContent = '';
    box.appendChild(frag);
    box.removeAttribute('aria-busy');
    /* restart the entrance every time, including page changes */
    box.classList.remove('is-fresh');
    void box.offsetWidth;
    box.classList.add('is-fresh');
    return box.children.length;
  }

  function pager(el, current, total, go) {
    if (!el) return;
    el.textContent = '';
    if (!total || total < 2) { el.hidden = true; return; }
    for (var p = 1; p <= total; p++) {
      (function (p) {
        var b = document.createElement('button');
        b.type = 'button';
        b.textContent = p;
        b.setAttribute('aria-label', 'Page ' + p);
        if (p === current) { b.className = 'is-cur'; b.setAttribute('aria-current', 'true'); }
        b.addEventListener('click', function () { go(p); });
        el.appendChild(b);
      })(p);
    }
    el.hidden = false;
  }

  /* ---------- Judge.me's thank-you step ----------
     Their wizard mounts on <body> with every step already in the DOM, so we
     can retype the last one before the reviewer ever slides to it. CSS does
     the layout (dp-reviews.css); this is only the words, which are theirs
     and read like a support macro. Same strings can be set in the Judge.me
     admin under Translations — this keeps them with the theme. */
  var THANKS = {
    eyebrow: 'Review received',
    title: 'Thanks for writing it.',
    intro: 'It goes up on the product page as soon as we have read it over.',
    text: 'Check your inbox and click the link we just sent. That is how every review on this site stays attached to a real person.',
    shopTitle: 'How was the rest of it?',
    shopIntro: 'The shop, the shipping, the packaging. Tell us where we are falling short.',
    close: 'Done'
  };

  function dressThankYou(modal) {
    var page = modal.querySelector('.jdgm-write-review-modal__page--thank-you');
    if (!page || page.__dpDressed) return;
    page.__dpDressed = true;
    var titles = page.querySelectorAll('.jdgm-write-review-modal__title');
    var intros = page.querySelectorAll('.jdgm-write-review-modal__intro');
    var note = page.querySelector('.jdgm-write-review-modal__text');
    var close = page.querySelector('.jdgm-write-review-modal__nav-btn-close');
    if (titles[0]) {
      titles[0].textContent = THANKS.title;
      var eb = document.createElement('p');
      eb.className = 'dp-jm-eyebrow';
      eb.textContent = THANKS.eyebrow;
      titles[0].parentNode.insertBefore(eb, titles[0]);
    }
    if (intros[0]) intros[0].textContent = THANKS.intro;
    if (note) note.textContent = THANKS.text;
    if (titles[1]) titles[1].textContent = THANKS.shopTitle;
    if (intros[1]) intros[1].textContent = THANKS.shopIntro;
    if (close) close.textContent = THANKS.close;
  }

  /* childList on body only — the modal mounts as a direct child, so this
     never walks the tree */
  if ('MutationObserver' in window) {
    var seen = function (n) {
      if (n.nodeType !== 1) return;
      if (n.classList && n.classList.contains('jdgm-write-review-modal')) dressThankYou(n);
    };
    new MutationObserver(function (muts) {
      muts.forEach(function (m) { Array.prototype.forEach.call(m.addedNodes, seen); });
    }).observe(document.body, { childList: true });
    document.querySelectorAll('body > .jdgm-write-review-modal').forEach(dressThankYou);
  }

  return { fetchPage: fetchPage, warm: warm, skeleton: skeleton, paint: paint, pager: pager,
           stars: stars, ago: ago, cap: cap, esc: esc, plain: plain, dressThankYou: dressThankYou };
})();
